import React from "react";
import {
  Box,
  ButtonGroup,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Text,
  useRadio,
  useRadioGroup,
  useColorMode,
  useToken,
} from "@chakra-ui/react";
import { useController } from "react-hook-form";

export function SegmentedControl({
  name,
  label,
  control,
  options,
  helperText,
  size = "md",
  isRequired = false,
  isRequiredMessage = "Please select an option",
  ...rest
}) {
  const {
    field: { value, onChange, ref },
    formState: { errors },
  } = useController({
    control,
    name,
    rules: {
      required: { value: isRequired, message: isRequiredMessage },
    },
  });

  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value,
    onChange,
  });

  return (
    <FormControl
      as="fieldset"
      isInvalid={!!errors[name]}
      isRequired={isRequired}
      {...rest}
    >
      <FormLabel as="legend">{label}</FormLabel>
      <ButtonGroup {...getRootProps()} isAttached variant="outline" size={size}>
        {options.map(({ label, value }, i) => {
          const radio = getRadioProps({ value });
          return (
            <SegmentButton
              key={value}
              size={size}
              {...radio}
              ref={i === 0 ? ref : undefined}
            >
              {label}
            </SegmentButton>
          );
        })}
      </ButtonGroup>
      {errors[name] ? (
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      ) : (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

const SegmentButton = React.forwardRef(({ size, ...props }, ref) => {
  const { colorMode } = useColorMode();
  const [brand500] = useToken("colors", ["brand.500"]);

  const { getInputProps, getRadioProps } = useRadio(props);
  const inputProps = getInputProps();
  const radioProps = getRadioProps();

  return (
    <Box as="label" ref={ref}>
      <input {...inputProps} />
      <Box
        {...radioProps}
        cursor="pointer"
        borderWidth="1px"
        borderColor={colorMode === "light" ? "gray.200" : "whiteAlpha.300"}
        _checked={{
          bg: colorMode === "light" ? "brand.500" : "brand.200",
          color: colorMode === "light" ? "white" : "gray.900",
          borderColor: colorMode === "light" ? "brand.500" : "brand.200",
          _hover: {
            bg: colorMode === "light" ? "brand.600" : "brand.300",
            color: colorMode === "light" ? "white" : "gray.900",
          },
        }}
        _focus={{
          borderColor: brand500,
          boxShadow: `0 0 0 1px ${brand500}`,
        }}
        _hover={{
          bg: colorMode === "light" ? "brand.50" : "rgba(129, 230, 217, 0.12)",
        }}
        px={size === "sm" ? 3 : 4}
        py={size === "sm" ? 1 : 2}
      >
        <Text fontSize={size === "sm" ? "sm" : "md"}>{props.children}</Text>
      </Box>
    </Box>
  );
});
